import type {
  ChangeArtworkStatusRequest,
  ChangeArtworkStatusResponse,
  CreateCreatorPaymentMethodRequest,
  CreateGalleryArtworkRequest,
  CreateProvenanceRequest,
  CreatorPaymentMethodResponse,
  DashboardArtworksResponse,
  DashboardChangeStatusRequest,
  GalleryAccountStatusResponse,
  GalleryArtworkResponse,
  GallerySubscriptionCheckoutResponse,
  GallerySubscriptionPortalResponse,
  GallerySubscriptionResponse,
  GalleryUpgradeRequestResponse,
  MarketplaceFilterParams,
  MarketplaceListingResponse,
  PaginatedParams,
  ProfileResponse,
  ProvenanceResponse,
  SubmitGalleryUpgradeRequest,
  UpdateArtworkRequest,
  UpdateArtworkResponse,
  UpdateCreatorPaymentMethodRequest,
  UpdateProfileRequest,
} from '@/types';
import { httpClient } from './httpClient';

const BASE = '/api/art';

// Marketplace

export async function getMarketplaceListings(
  params?: MarketplaceFilterParams,
): Promise<MarketplaceListingResponse[]> {
  const response = await httpClient.get<MarketplaceListingResponse[]>(`${BASE}/marketplace`, {
    params,
  });

  return response.data;
}

export async function getMarketplaceListing(id: string): Promise<MarketplaceListingResponse> {
  const response = await httpClient.get<MarketplaceListingResponse>(`${BASE}/marketplace/${id}`);

  return response.data;
}

// Dashboard artworks

export async function getDashboardArtworks(
  params?: PaginatedParams,
): Promise<DashboardArtworksResponse> {
  const response = await httpClient.get<DashboardArtworksResponse>(`${BASE}/dashboard/artworks`, {
    params,
  });

  return response.data;
}

export async function changeDashboardArtworkStatus(
  id: string,
  data: DashboardChangeStatusRequest,
): Promise<ChangeArtworkStatusResponse> {
  const response = await httpClient.patch<ChangeArtworkStatusResponse>(
    `${BASE}/dashboard/artworks/${id}/status`,
    data,
  );

  return response.data;
}

export async function approvePublication(artworkId: string): Promise<void> {
  await httpClient.post(`${BASE}/admin/artworks/${artworkId}/publication/approve`);
}

export async function rejectPublication(artworkId: string, note: string): Promise<void> {
  await httpClient.post(`${BASE}/admin/artworks/${artworkId}/publication/reject`, { note });
}

export async function sendBackPublication(artworkId: string, note: string): Promise<void> {
  await httpClient.post(`${BASE}/admin/artworks/${artworkId}/publication/send-back`, { note });
}

export async function createGalleryArtwork(
  data: CreateGalleryArtworkRequest,
): Promise<GalleryArtworkResponse> {
  const response = await httpClient.post<GalleryArtworkResponse>(
    `${BASE}/dashboard/gallery/artworks`,
    data,
  );

  return response.data;
}

// Dashboard profile

export async function getProfile(): Promise<ProfileResponse> {
  const response = await httpClient.get<ProfileResponse>(`${BASE}/dashboard/profile`);

  return response.data;
}

export async function updateProfile(data: UpdateProfileRequest): Promise<ProfileResponse> {
  const response = await httpClient.patch<ProfileResponse>(`${BASE}/dashboard/profile`, data);

  return response.data;
}

export async function getCreatorPaymentMethods(): Promise<CreatorPaymentMethodResponse[]> {
  const response = await httpClient.get<CreatorPaymentMethodResponse[]>(
    `${BASE}/dashboard/profile/payment-methods`,
  );

  return response.data;
}

export async function createCreatorPaymentMethod(
  data: CreateCreatorPaymentMethodRequest,
): Promise<CreatorPaymentMethodResponse> {
  const response = await httpClient.post<CreatorPaymentMethodResponse>(
    `${BASE}/dashboard/profile/payment-methods`,
    data,
  );

  return response.data;
}

export async function updateCreatorPaymentMethod(
  id: string,
  data: UpdateCreatorPaymentMethodRequest,
): Promise<CreatorPaymentMethodResponse> {
  const response = await httpClient.patch<CreatorPaymentMethodResponse>(
    `${BASE}/dashboard/profile/payment-methods/${id}`,
    data,
  );

  return response.data;
}

export async function deleteCreatorPaymentMethod(id: string): Promise<void> {
  await httpClient.delete(`${BASE}/dashboard/profile/payment-methods/${id}`);
}

export async function submitGalleryUpgradeRequest(
  data: SubmitGalleryUpgradeRequest,
): Promise<GalleryUpgradeRequestResponse> {
  const response = await httpClient.post<GalleryUpgradeRequestResponse>(
    `${BASE}/dashboard/profile/gallery-account/request`,
    data,
  );

  return response.data;
}

export async function getGalleryAccountStatus(): Promise<GalleryAccountStatusResponse> {
  const response = await httpClient.get<GalleryAccountStatusResponse>(
    `${BASE}/dashboard/profile/gallery-account/status`,
  );

  return response.data;
}

export async function getGallerySubscription(): Promise<GallerySubscriptionResponse> {
  const response = await httpClient.get<GallerySubscriptionResponse>(
    `${BASE}/dashboard/gallery/subscription`,
  );

  return response.data;
}

export async function createGallerySubscriptionCheckout(
  consentStartNow = false,
): Promise<GallerySubscriptionCheckoutResponse> {
  const response = await httpClient.post<GallerySubscriptionCheckoutResponse>(
    `${BASE}/dashboard/gallery/subscription/checkout`,
    { consent_start_now: consentStartNow },
  );

  return response.data;
}

export async function createGallerySubscriptionPortal(): Promise<GallerySubscriptionPortalResponse> {
  const response = await httpClient.post<GallerySubscriptionPortalResponse>(
    `${BASE}/dashboard/gallery/subscription/portal`,
  );

  return response.data;
}

export async function reconcileGallerySubscription(): Promise<GallerySubscriptionResponse> {
  const response = await httpClient.post<GallerySubscriptionResponse>(
    `${BASE}/dashboard/gallery/subscription/reconcile`,
  );

  return response.data;
}

// Artwork CRUD

export async function updateArtwork(
  uuid: string,
  data: UpdateArtworkRequest,
): Promise<UpdateArtworkResponse> {
  const response = await httpClient.patch<UpdateArtworkResponse>(`${BASE}/artwork/${uuid}`, data);

  return response.data;
}

export async function changeArtworkStatus(
  uuid: string,
  data: ChangeArtworkStatusRequest,
): Promise<ChangeArtworkStatusResponse> {
  const response = await httpClient.patch<ChangeArtworkStatusResponse>(
    `${BASE}/artwork/${uuid}/status`,
    data,
  );

  return response.data;
}

// Provenance

export async function getProvenanceRecords(
  uuid: string,
  params?: PaginatedParams,
): Promise<ProvenanceResponse[]> {
  const response = await httpClient.get<ProvenanceResponse[]>(`${BASE}/provenances/${uuid}`, {
    params,
  });

  return response.data;
}

export async function getProvenanceRecord(uuid: string): Promise<ProvenanceResponse> {
  const response = await httpClient.get<ProvenanceResponse>(`${BASE}/provenance/${uuid}`);

  return response.data;
}

// Provenance drafts. Owner and artist drafts use the proposals routes, gallery drafts the gallery
// routes. A draft is not provenance until it is submitted and approved.

const DASHBOARD_ART = `${BASE}/dashboard/artworks`;
const DASHBOARD_GALLERY_ART = `${BASE}/dashboard/gallery/artworks`;

export async function listProposals(artworkID: string): Promise<ProvenanceResponse[]> {
  const response = await httpClient.get<ProvenanceResponse[]>(
    `${DASHBOARD_ART}/${artworkID}/provenance/proposals`,
  );

  return response.data;
}

export async function createProposal(
  artworkID: string,
  data: CreateProvenanceRequest,
): Promise<ProvenanceResponse> {
  const response = await httpClient.post<ProvenanceResponse>(
    `${DASHBOARD_ART}/${artworkID}/provenance/proposals`,
    data,
  );

  return response.data;
}

export async function updateProposal(
  artworkID: string,
  recordID: string,
  data: Partial<CreateProvenanceRequest>,
): Promise<ProvenanceResponse> {
  const response = await httpClient.patch<ProvenanceResponse>(
    `${DASHBOARD_ART}/${artworkID}/provenance/proposals/${recordID}`,
    data,
  );

  return response.data;
}

export async function deleteProposal(artworkID: string, recordID: string): Promise<void> {
  await httpClient.delete(`${DASHBOARD_ART}/${artworkID}/provenance/proposals/${recordID}`);
}

export async function submitProposal(
  artworkID: string,
  recordID: string,
): Promise<ProvenanceResponse> {
  const response = await httpClient.post<ProvenanceResponse>(
    `${DASHBOARD_ART}/${artworkID}/provenance/proposals/${recordID}/submit`,
  );

  return response.data;
}

export async function listGalleryProvenance(artworkID: string): Promise<ProvenanceResponse[]> {
  const response = await httpClient.get<ProvenanceResponse[]>(
    `${DASHBOARD_GALLERY_ART}/${artworkID}/provenance`,
  );

  return response.data;
}

export async function createGalleryProvenance(
  artworkID: string,
  data: CreateProvenanceRequest,
): Promise<ProvenanceResponse> {
  const response = await httpClient.post<ProvenanceResponse>(
    `${DASHBOARD_GALLERY_ART}/${artworkID}/provenance`,
    data,
  );

  return response.data;
}

export async function updateGalleryProvenance(
  artworkID: string,
  recordID: string,
  data: Partial<CreateProvenanceRequest>,
): Promise<ProvenanceResponse> {
  const response = await httpClient.patch<ProvenanceResponse>(
    `${DASHBOARD_GALLERY_ART}/${artworkID}/provenance/${recordID}`,
    data,
  );

  return response.data;
}

export async function deleteGalleryProvenance(artworkID: string, recordID: string): Promise<void> {
  await httpClient.delete(`${DASHBOARD_GALLERY_ART}/${artworkID}/provenance/${recordID}`);
}

export async function submitGalleryProvenance(
  artworkID: string,
  recordID: string,
): Promise<ProvenanceResponse> {
  const response = await httpClient.post<ProvenanceResponse>(
    `${DASHBOARD_GALLERY_ART}/${artworkID}/provenance/${recordID}/submit`,
  );

  return response.data;
}
